import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Spin, message } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface DiscountRateData {
  discount_type: string;
  discount_value: number;
  discount_label: string;
  issued_count: number;
  used_count: number;
  usage_rate: number;
  avg_order_value: number;
  template_count: number;
}

const DiscountRateUsage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DiscountRateData[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/discount-rate-usage');

      if (response.data.success) {
        setData(response.data.data);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('値引き率別使用率分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      '値引き種類': row.discount_label,
      '発行数': row.issued_count,
      '使用数': row.used_count,
      '使用率': `${row.usage_rate}%`,
      '平均注文額': row.avg_order_value ? `¥${Math.round(row.avg_order_value).toLocaleString()}` : '-',
      'テンプレート数': row.template_count
    }));

    downloadCSV(csvData, '値引き率別使用率分析.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  // 使用率グラフデータ
  const usageRateChartData = {
    labels: data.map(d => d.discount_label),
    datasets: [
      {
        label: '使用率 (%)',
        data: data.map(d => d.usage_rate),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  };

  // 平均注文額グラフデータ
  const avgOrderChartData = {
    labels: data.map(d => d.discount_label),
    datasets: [
      {
        label: '平均注文額 (円)',
        data: data.map(d => Math.round(d.avg_order_value || 0)),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const columns = [
    {
      title: '値引き種類',
      dataIndex: 'discount_label',
      key: 'discount_label',
      width: 150,
      render: (text: string) => (
        <span className="font-bold">{text}</span>
      )
    },
    {
      title: '発行数',
      dataIndex: 'issued_count',
      key: 'issued_count',
      align: 'right' as const,
      render: (count: number) => `${count}枚`,
      width: 100
    },
    {
      title: '使用数',
      dataIndex: 'used_count',
      key: 'used_count',
      align: 'right' as const,
      render: (count: number) => `${count}枚`,
      width: 100
    },
    {
      title: '使用率',
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      align: 'right' as const,
      render: (rate: number) => {
        const color = rate >= 70 ? 'text-green-600' : rate >= 40 ? 'text-yellow-600' : 'text-gray-500';
        return <span className={`font-bold ${color}`}>{rate}%</span>;
      },
      sorter: (a: DiscountRateData, b: DiscountRateData) => a.usage_rate - b.usage_rate,
      defaultSortOrder: 'descend' as const,
      width: 120
    },
    {
      title: '平均注文額',
      dataIndex: 'avg_order_value',
      key: 'avg_order_value',
      align: 'right' as const,
      render: (value: number) => value ? `¥${Math.round(value).toLocaleString()}` : '-',
      sorter: (a: DiscountRateData, b: DiscountRateData) =>
        (a.avg_order_value || 0) - (b.avg_order_value || 0),
      width: 140
    },
    {
      title: 'テンプレート数',
      dataIndex: 'template_count',
      key: 'template_count',
      align: 'right' as const,
      render: (count: number) => `${count}個`,
      width: 120
    }
  ];

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={data.length === 0}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">値引き率別使用率分析とは？</div>
            <div className="text-sm text-gray-600">
              値引き率（10%オフ、20%オフなど）ごとに使用率を比較します。
              どの値引き率が最も効果的かを把握し、最適な値引き設定を見つけられます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : (
        <>
          {/* 使用率グラフ */}
          <Card title="値引き率別使用率" className="mb-4">
            {data.length > 0 ? (
              <Bar data={usageRateChartData} options={chartOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>

          {/* 平均注文額グラフ */}
          <Card title="値引き率別平均注文額" className="mb-4">
            {data.length > 0 ? (
              <Bar data={avgOrderChartData} options={chartOptions} />
            ) : (
              <div className="text-center text-gray-500 py-12">
                データがありません
              </div>
            )}
          </Card>

          {/* データテーブル */}
          <Card title="値引き率別使用率（詳細）">
            <Table
              columns={columns}
              dataSource={data}
              rowKey={(record) => `${record.discount_type}_${record.discount_value}`}
              pagination={false}
              locale={{
                emptyText: '該当するデータがありません'
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default DiscountRateUsage;
